import React from "react";
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

function SpeakerCard({ instructor }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      whileHover={{ y: -5, transition: { duration: 0.3 } }}
      className="max-w-8xl bg-gradient-to-br from-white to-emerald-50 my-6 mx-auto px-4 sm:px-6 lg:px-8 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border-2 border-emerald-100 shadow-xl shadow-green-900/5 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
      <div className="absolute -top-20 -right-20 w-60 h-60 bg-green-200/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-emerald-200/20 rounded-full blur-3xl"></div>

      <motion.h3 
        className="text-xl font-bold text-gray-900 mb-6 flex items-center border-b border-emerald-200 pb-3 relative z-10"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
      >
        <motion.div
          animate={{ rotate: [0, 5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <Icon name="User" size={20} className="mr-3 text-green-600" />
        </motion.div>
        Meet Your Speaker
      </motion.h3>
      
      <div className="flex flex-col md:flex-row gap-6 relative z-10">
        {/* Speaker Image */}
        <motion.div 
          className="flex-shrink-0 mx-auto md:mx-0"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
        >
          <div className="relative">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <img
                src={instructor?.avatar || "/assets/images/instructor-placeholder.jpg"}
                alt={instructor?.name || "Dr. Sarah Johnson"}
                className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 rounded-full object-cover border-4 border-emerald-100 shadow-xl shadow-green-900/20"
              />
              <div className="absolute inset-0 rounded-full border-2 border-green-400 opacity-20"></div>
            </motion.div>
            
            <motion.div 
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center border-2 border-white shadow-lg"
              animate={{ 
                scale: [1, 1.2, 1],
                boxShadow: [
                  "0 0 0 0 rgba(16, 185, 129, 0)",
                  "0 0 0 8px rgba(16, 185, 129, 0.2)",
                  "0 0 0 0 rgba(16, 185, 129, 0)"
                ]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Icon name="CheckCircle" size={14} className="text-white" />
            </motion.div>
          </div>
          
          {/* Stats under image for medium+ screens */}
          <div className="hidden md:flex flex-col items-center mt-4 space-y-2">
            <div className="flex items-center space-x-1 text-sm text-emerald-700">
              <Icon name="Star" size={12} className="text-yellow-500" />
              <span className="font-semibold">Expert Speaker</span>
            </div>
            <div className="flex items-center space-x-1 text-sm text-green-600">
              <Icon name="Award" size={12} className="text-green-500" />
              <span>Certified Professional</span>
            </div>
          </div>
        </motion.div>

        {/* Speaker Info */}
        <div className="flex-1 text-center md:text-left">
          {/* Name and Title */}
          <motion.div 
            className="mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {instructor?.name || "Dr. Sarah Johnson"}
            </h4>
            
            {/* Title and Company */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <motion.p 
                className="text-lg bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent font-semibold"
                whileHover={{ scale: 1.02 }}
              >
                {instructor?.title || "Senior Tax Consultant & CPA"}
              </motion.p>
              <span className="text-emerald-400">•</span>
              <motion.p 
                className="text-sm text-gray-600 font-medium px-3 py-1 bg-green-50 rounded-full border border-green-100"
                whileHover={{ scale: 1.05 }}
              >
                {instructor?.company || "Tax Solutions Inc."}
              </motion.p>
            </div>
            
            {/* Stats for mobile */}
            <div className="flex md:hidden justify-center space-x-4 mt-3">
              <div className="flex items-center space-x-1 text-sm text-emerald-700">
                <Icon name="Star" size={12} className="text-yellow-500" />
                <span className="font-semibold">Expert</span>
              </div>
              <div className="flex items-center space-x-1 text-sm text-green-600">
                <Icon name="Award" size={12} className="text-green-500" />
                <span>Certified</span>
              </div>
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div 
            className="mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <div 
              className="webinar-content text-gray-900 leading-relaxed
                   [&>ul]:space-y-2 sm:[&>ul]:space-y-3 [&>ul]:my-4 sm:[&>ul]:my-6 [&>ul]:ml-0 [&>ul]:pl-0
                   [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:list-none
                   [&>ul>li]:text-gray-900 [&>ul>li]:text-sm sm:[&>ul>li]:text-base [&>ul>li]:leading-relaxed
                   
                   [&>h1]:text-gray-900 [&>h1]:text-xl sm:[&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mt-4 sm:[&>h1]:mt-6 [&>h1]:mb-3 sm:[&>h1]:mb-4 [&>h1]:border-b [&>h1]:border-emerald-600/50 [&>h1]:pb-2
                   [&>h2]:text-gray-900 [&>h2]:text-lg sm:[&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-4 sm:[&>h2]:mt-5 [&>h2]:mb-2 sm:[&>h2]:mb-3
                   [&>h3]:text-gray-900 [&>h3]:text-base sm:[&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mt-3 sm:[&>h3]:mt-4 [&>h3]:mb-2
                   [&>h4]:text-gray-800 [&>h4]:text-sm sm:[&>h4]:text-base [&>h4]:font-semibold [&>h4]:mt-2 sm:[&>h4]:mt-3 [&>h4]:mb-2
                   [&>p]:text-gray-900 [&>p]:mb-3 sm:[&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-sm sm:[&>p]:text-base
                   [&>strong]:text-gray-900 [&>strong]:text-lg sm:[&>strong]:text-xl [&>strong]:font-bold
                   [&>b]:text-gray-900 [&>b]:font-bold
                   [&>em]:text-emerald-700 [&>em]:italic
                   [&>a]:text-green-600 [&>a]:underline [&>a]:font-medium hover:[&>a]:text-green-800
                   [&>blockquote]:border-l-4 [&>blockquote]:border-green-600 [&>blockquote]:pl-4 sm:[&>blockquote]:pl-6 [&>blockquote]:py-3 sm:[&>blockquote]:py-4 [&>blockquote]:my-4 sm:[&>blockquote]:my-6 [&>blockquote]:italic [&>blockquote]:text-emerald-800 [&>blockquote]:bg-green-50
                   [&>code]:bg-emerald-50 [&>code]:text-gray-900 [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-xs [&>code]:border [&>code]:border-emerald-200
                   [&>pre]:bg-emerald-50 [&>pre]:text-gray-900 [&>pre]:p-3 sm:[&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-3 sm:[&>pre]:my-4 [&>pre]:border [&>pre]:border-emerald-200
                   [&>img]:max-w-full [&>img]:h-auto [&>img]:rounded-lg [&>img]:my-4 sm:[&>img]:my-6 [&>img]:mx-auto [&>img]:shadow-lg [&>img]:border [&>img]:border-emerald-100
                   [&>table]:w-full [&>table]:bg-white [&>table]:rounded-lg [&>table]:overflow-hidden [&>table]:my-4 sm:[&>table]:my-6 [&>table]:border [&>table]:border-emerald-200
                   [&>th]:text-gray-900 [&>th]:font-semibold [&>th]:p-2 sm:[&>th]:p-3 [&>th]:text-left [&>th]:bg-green-50 [&>th]:border-b [&>th]:border-emerald-200 [&>th]:text-xs sm:[&>th]:text-sm
                   [&>td]:p-2 sm:[&>td]:p-3 [&>td]:text-gray-900 [&>td]:border-b [&>td]:border-emerald-100 [&>td]:text-xs sm:[&>td]:text-sm
                   [&>hr]:border-0 [&>hr]:h-px [&>hr]:bg-gradient-to-r [&>hr]:from-transparent [&>hr]:via-green-600 [&>hr]:to-transparent [&>hr]:my-4 sm:[&>hr]:my-6"
              dangerouslySetInnerHTML={{ __html: instructor?.bio }}
            />
          </motion.div>
        </div>
      </div>
      
      {/* Footer contact info or social links placeholder */}
      <motion.div 
        className="mt-6 pt-4 border-t border-emerald-100 flex flex-wrap justify-center md:justify-start gap-4 text-sm text-gray-600"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-center space-x-2">
          <Icon name="Mail" size={14} className="text-green-600" />
          <span>{instructor?.email || "Contact via platform"}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Briefcase" size={14} className="text-emerald-600" />
          <span>{instructor?.experience || "15+ years experience"}</span>
        </div>
      </motion.div>
      
      <style jsx>{`
        .webinar-content ul {
          margin: 16px 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .webinar-content ul li {
          display: flex;
          align-items: flex-start;
          list-style: none;
          color: rgb(17, 24, 39);
          font-size: 14px;
          line-height: 1.6;
          font-weight: 500;
          position: relative;
          padding-left: 10px;
        }
        
        .webinar-content ul li::before {
          content: '✓';
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: linear-gradient(to right, #10b981, #059669);
          border-radius: 50%;
          color: white;
          font-size: 11px;
          font-weight: bold;
          margin-right: 12px;
          margin-top: 2px;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          position: absolute;
          left: -10px;
        }
        
        @media (min-width: 640px) {
          .webinar-content ul li::before {
            width: 24px;
            height: 24px;
            margin-right: 16px;
            font-size: 12px;
          }
        }
        
        .webinar-content h1 {
          color: rgb(17, 24, 39);
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1.5rem 0 1rem 0;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid rgba(16, 185, 129, 0.5);
          position: relative;
        }
        
        .webinar-content h1::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(to right, #10b981, #059669);
        }
        
        .webinar-content em {
          color: rgb(4, 120, 87);
          font-style: italic;
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 100%);
          padding: 0 2px;
          border-radius: 2px;
        }
        
        .webinar-content a {
          color: rgb(5, 150, 105);
          text-decoration: none;
          font-weight: 600;
          position: relative;
          transition: all 0.3s ease;
          padding: 0 2px;
          border-radius: 2px;
        }
        
        .webinar-content a:hover {
          color: rgb(6, 95, 70);
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.3) 100%);
        }
        
        .webinar-content a::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 100%;
          height: 1px;
          background: linear-gradient(to right, #10b981, #059669);
          transform: scaleX(0);
          transform-origin: right;
          transition: transform 0.3s ease;
        }
        
        .webinar-content a:hover::after {
          transform: scaleX(1);
          transform-origin: left;
        }
        
        .webinar-content blockquote {
          border-left: 4px solid rgb(5, 150, 105);
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: rgb(4, 120, 87);
          border-radius: 0 8px 8px 0;
          position: relative;
          overflow: hidden;
        }
        
        .webinar-content blockquote::before {
          content: '"';
          position: absolute;
          top: -10px;
          left: 10px;
          font-size: 60px;
          color: rgba(5, 150, 105, 0.1);
          font-family: Georgia, serif;
        }
        
        .webinar-content code {
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
          border: 1px solid rgba(16, 185, 129, 0.3);
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }
        
        .webinar-content pre {
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
          border: 1px solid rgba(16, 185, 129, 0.2);
          position: relative;
        }
        
        .webinar-content pre::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(to bottom, #10b981, #059669);
          border-radius: 4px 0 0 4px;
        }
        
        .webinar-content img {
          border: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .webinar-content img:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.25);
        }
        
        .webinar-content table {
          border: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 4px 6px rgba(16, 185, 129, 0.1);
        }
        
        .webinar-content th {
          background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.15) 100%);
        }
        
        .webinar-content tr:hover {
          background: rgba(16, 185, 129, 0.05);
        }
        
        .webinar-content hr {
          height: 2px;
          background: linear-gradient(to right, transparent, #10b981, transparent);
          border: none;
          margin: 1.5rem 0;
          position: relative;
        }
        
        .webinar-content hr::before {
          content: '◆';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #10b981;
          background: white;
          padding: 0 10px;
          font-size: 12px;
        }
      `}</style>
    </motion.div>
  );
}

export default SpeakerCard;